import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import CTabs, { CTabPanel } from "../../components/CTabs";
import CButton from "../../components/CButton";
import CSwitchToggle from "../../components/CSwitchToggle";
import CDatePicker from "../../components/CDatePicker";
import { ReactComponent as EditIcon } from "../../assets/images/edit-icon.svg";
import CSelect from "../../components/CSelect";
import BudgetAllocation from "./BudgetAllocation";
import BBBudgetConfiguration from "./BBBudgetConfiguration";
import { supabase, fromWithCompany } from "../../config/supabaseClient";
import { toast } from "react-toastify";
import {
  fetchConfiguration,
  updateConfiguration,
  handleTrainingNeedCollectionToggle,
} from "../../slices/configSlice";
import { getLocalData } from "../../constant/utils/commonUtils";
import { currentDate, getDate } from "../../constant/utils/datetimeUtils";
import TotalBudget from "./TotalBudget";
import { fetchYearId } from "../../slices/yearSlice";
import moment from "moment";
const tabs = [
  { label: "Form Configuration", value: "Form Configuration" },
  { label: "Budget Configuration", value: "Budget Configuration" },
  {
    label: "B-BBEE Budget Configuration",
    value: "B-BBEE Budget Configuration",
  },
  {
    label: "Training Type Budget Configuration",
    value: "Training Type Budget Configuration",
  },
];

const TrainingConfiguration = () => {
  const dispatch = useDispatch();
  const { configData, loading } = useSelector((state) => state.config);
  const [activeTab, setActiveTab] = useState("Form Configuration");
  const [yearId, setYearId] = useState(null);
  const [localData, setLocalData] = useState(null);

  const loadLocalData = () => {
    const userData = getLocalData("sdfUser");
    if (userData) {
      setLocalData(userData);
    }
    return userData;
  };

  useEffect(() => {
    dispatch(fetchYearId()).then((res) => {
      if (res.payload) {
        setYearId(res.payload);
      } else {
        toast.error("No year found");
      }
    });
  }, []);

  useEffect(() => {
    if (yearId) {
      const userData = loadLocalData();
      dispatch(
        fetchConfiguration({
          yearId,
          companyId: userData?.company_id,
        })
      );
    }
  }, [yearId]);

  // const fetchYearId = async () => {
  //   try {
  //     const { data, error } = await fromWithCompany("year")
  //       .select("id")
  //       .eq("status", true)
  //       .lte("start_date", currentDate())
  //       .gte("end_date", currentDate())
  //       .single();

  //     if (error) {
  //       console.error("Error fetching year_id:", error);
  //       return;
  //     }

  //     if (data) {
  //       setYearId(data.id);
  //     }
  //   } catch (error) {
  //     console.error("Error in fetchYearId:", error);
  //   }
  // };

  const handleTrainingNeedCollectionToggle = async (checked) => {
    try {
      if (!yearId || !getLocalData("sdfUser")?.company_id) {
        console.error("Missing required data:", {
          yearId,
          companyId: getLocalData("sdfUser")?.company_id,
        });
        return;
      }

      // Set current date for both ON and OFF states
      const updateData = {
        training_need_collection_start_date: checked ? currentDate() : null,
      };

      const result = await dispatch(
        updateConfiguration({
          yearId,
          companyId: getLocalData("sdfUser")?.company_id,
          updateData,
        })
      ).unwrap();

    } catch (error) {
      console.error("Error in handleTrainingNeedCollectionToggle:", error);
    }
  };

  const handleDateUpdate = async (fieldName, date) => {
       try {
         // If updating employee submission date, validate against manager review date
         if (fieldName === "employee_submission_date" && date) {
           const managerReviewDate = configData?.manager_review_submission_date;
           if (
             managerReviewDate &&
             moment(date).isAfter(moment(managerReviewDate), "day")
           ) {
             toast.error(
               "Employee submission date cannot be after manager review date"
             );
             return;
           }
         }
   
   
   
         // If updating manager review date, validate against employee submission date
         if (fieldName === "manager_review_submission_date" && date) {
           const employeeSubmissionDate = configData?.employee_submission_date;
           if (
             employeeSubmissionDate &&
             moment(date).isBefore(moment(employeeSubmissionDate), "day")
           ) {
             toast.error(
               "Manager review date cannot be before employee submission date"
             );
             return;
           }
         }
         await dispatch(
           updateConfiguration({
             yearId,
             companyId: getLocalData("sdfUser")?.company_id,
             updateData: {
               [fieldName]: moment(date).startOf("day").format("YYYY-MM-DD"),
             },
           })
         ).unwrap();
       } catch (error) {
         console.error("Error updating date:", error);
       }
     };

  const handleReminderIntervalChange = async (event) => {
    try {
      await dispatch(
        updateConfiguration({
          yearId,
          companyId: getLocalData("sdfUser")?.company_id,
          updateData: { reminder_interval: parseInt(event.target.value) },
        })
      ).unwrap();
    } catch (error) {
      console.error("Error updating reminder interval:", error);
    }
  };

  const handleToggleUpdate = async (fieldName, checked) => {
    try {
      const result = await dispatch(
        updateConfiguration({
          yearId,
          companyId: getLocalData("sdfUser")?.company_id,
          updateData: { [fieldName]: checked },
        })
      ).unwrap();
      // If this is select_training being turned off, also turn off add_adhoc_training
      if (fieldName === "select_training" && !checked) {
        await dispatch(
          updateConfiguration({
            yearId,
            companyId: getLocalData("sdfUser")?.company_id,
            updateData: { add_adhoc_training: false },
          })
        ).unwrap();
      }
    } catch (error) {
      console.error(`Error updating ${fieldName}:`, error);
      toast.error(`Error updating ${fieldName}`);
    }
  };

  return (
    <div className="min-h-screen">
      <h2 className="2xl:text-3xl 2xl:leading-3xl text-2xl leading-2xl text-blue-500 font-semibold">
        Training Strategy
      </h2>
      <div className="2xl:py-6 py-4">
        <div className="flex items-center justify-between pb-2 border-b border-blue-200">
          <div>
            <h3 className="2xl:text-2xl 2xl:leading-2xl text-xl leading-xl text-gray-700 font-semibold">
              Configure
            </h3>
            <p className="2xl:text-base 2xl:leading-base text-sm leading-sm font-medium text-gray-300">
              Enable the training needs workflow form for capturing the training
              needs of the employees along with the recommendation of the line
              managers.
            </p>
          </div>

          <div>
            <CSwitchToggle
              className="green-switch-btn"
              checked={configData?.training_need_workflow ?? false}
              onChange={(e) =>
                handleToggleUpdate("training_need_workflow", e.target.checked)
              }
              disabled={loading}
            />
          </div>
        </div>

        {configData?.training_need_workflow ? (
          <>
            <CTabs
              tabs={tabs}
              value={activeTab}
              onChange={setActiveTab}
              className="mb-4 mt-6"
            />

            <CTabPanel value={activeTab} index="Form Configuration">
              {/* Process Configuration Section */}
              <div className="bg-white rounded-lg p-6 border border-blue-200 relative mt-4">
                <p className="text-gray-900 font-semibold 2xl:text-xl 2xl:leading-xl text-base leading-base mb-1 absolute top-[-14px] left-6 bg-white px-2">
                  Process Configuration
                </p>

                <div className="bg-blue-100 rounded-lg px-4 pr-0 py-2 mb-2 flex items-center justify-between">
                  <p className="text-gray-700 font-semibold 2xl:text-base 2xl:leading-base text-sm leading-sm">
                    Form Sections
                  </p>
                </div>

                <div className="ml-4">
                  <p className="2xl:text-base 2xl:leading-base text-sm leading-sm font-medium text-gray-300">
                    Enable the starting of process and allow to choose the
                    training from training catalogue
                  </p>
                </div>

                <div className="space-y-2 my-3">
                  <div className="flex items-center justify-between pl-10">
                    <span className="font-medium text-gray-700 2xl:text-base 2xl:leading-base text-sm leading-sm">
                      Start the training needs collection process
                    </span>
                    <CSwitchToggle
                      className="green-switch-btn"
                      checked={Boolean(
                        configData?.training_need_collection_start_date
                      )}
                      onChange={(e) => {
                        const newCheckedState = e.target.checked;
                        handleTrainingNeedCollectionToggle(newCheckedState);
                      }}
                      disabled={loading}
                    />
                  </div>

                  <div className="flex items-center justify-between pl-10">
                    <span className="font-medium text-gray-700 2xl:text-base 2xl:leading-base text-sm leading-sm">
                      Enable employees to select training from the training
                      catalogue
                    </span>
                    <CSwitchToggle
                      className="green-switch-btn"
                      checked={configData?.select_training ?? false}
                      onChange={(e) =>
                        handleToggleUpdate("select_training", e.target.checked)
                      }
                      disabled={loading}
                    />
                  </div>

                  <div className="flex items-center justify-between pl-20">
                    <span className="font-medium text-gray-700 2xl:text-base 2xl:leading-base text-sm leading-sm">
                      Allow employees to add-hoc training
                    </span>
                    <CSwitchToggle
                      className="green-switch-btn"
                      checked={configData?.add_adhoc_training ?? false}
                      onChange={(e) =>
                        handleToggleUpdate(
                          "add_adhoc_training",
                          e.target.checked
                        )
                      }
                      disabled={loading || !configData?.select_training}
                    />
                  </div>
                </div>

                {/* Time Period Configuration Section */}
                <div className="relative">
                  <div className="bg-blue-100 rounded-lg px-4 pr-0 py-2 mb-2 flex items-center justify-between">
                    <p className="text-gray-700 font-semibold 2xl:text-base 2xl:leading-base text-sm leading-sm">
                      Time Period Configuration
                    </p>
                  </div>

                  <div className="mb-6 pl-10">
                    <h3 className="font-semibold 2xl:text-base 2xl:leading-base text-sm leading-sm mb-1 text-gray-700">
                      Employee Submission period
                    </h3>
                    <p className="2xl:text-base 2xl:leading-base text-sm leading-sm text-gray-300 mb-3">
                      Define the time period for employee to submit there
                      training needs form.
                    </p>
                    <CDatePicker
                      label="Process Period"
                      value={
                        configData?.employee_submission_date
                          ? new Date(configData.employee_submission_date)
                          : null
                      }
                      onChange={(date) =>
                        handleDateUpdate("employee_submission_date", date)
                      }
                      className="!flex-row items-center gap-3"
                      maxDate={
                        configData?.manager_review_submission_date
                          ? new Date(configData.manager_review_submission_date)
                          : undefined
                      }
                    />
                  </div>

                  <div className="mb-6 pl-10">
                    <h3 className="font-semibold 2xl:text-base 2xl:leading-base text-sm leading-sm mb-1 text-gray-700">
                      Manager Review Submission period
                    </h3>
                    <p className="2xl:text-base 2xl:leading-base text-sm leading-sm text-gray-300 mb-3">
                      Define the time period for manager to submit there
                      training needs form.
                    </p>
                    <CDatePicker
                      label="Process Period"
                      value={
                        configData?.manager_review_submission_date
                          ? new Date(configData.manager_review_submission_date)
                          : null
                      }
                      onChange={(date) =>
                        handleDateUpdate("manager_review_submission_date", date)
                      }
                      className="!flex-row items-center gap-3"
                      minDate={
                        configData?.employee_submission_date
                          ? new Date(configData.employee_submission_date)
                          : undefined
                      }
                    />
                  </div>
                </div>
                <div className="relative">
                  <div className="bg-blue-100 rounded-lg px-4 pr-0 py-2 mb-2 flex items-center justify-between">
                    <p className="text-gray-700 font-semibold 2xl:text-base 2xl:leading-base text-sm leading-sm">
                      Reminder Configuration
                    </p>
                  </div>

                  <div className="mb-6 pl-10">
                    <h3 className="font-semibold 2xl:text-base 2xl:leading-base text-sm leading-sm mb-1 text-gray-700">
                      Reminder Interval
                    </h3>
                    <p className="2xl:text-base 2xl:leading-base text-sm leading-sm text-gray-300 mb-3">
                      Define the interval period between each reminder till end
                      date
                    </p>
                    <CSelect
                      name="reminder_interval"
                      label="Reminder"
                      placeholder="Select Reminder Interval"
                      value={configData?.reminder_interval?.toString() || ""}
                      onChange={handleReminderIntervalChange}
                      options={[
                        { id: 1, value: "1", label: "Weekly" },
                        { id: 2, value: "2", label: "Twice a week" },
                        { id: 3, value: "3", label: "Daily" },
                      ]}
                      formControlClass="!flex-row items-center gap-3"
                      className="min-w-[250px]"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </CTabPanel>

            <CTabPanel value={activeTab} index="Budget Configuration">
              <BudgetAllocation />
            </CTabPanel>

            <CTabPanel
              value={activeTab}
              index="B-BBEE Budget Configuration"
            >
              <BBBudgetConfiguration />
            </CTabPanel>
            <CTabPanel value={activeTab} index="Training Type wise budget Configuration">
              <TotalBudget />
            </CTabPanel>
          </>
        ) : (
          <div className="text-center text-gray-500 mt-8">
            <p className="text-lg">
              Please enable the training needs workflow form to access
              configuration options.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingConfiguration;
